/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { TextField, Grid, Chip, Checkbox, Tooltip, Typography, CardContent } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { CONFIG_TOOL_TIPS, USER_MANAGEMENT_TOOL_TIPS } from "../../../../utility/toolTipMessages";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import { isValidAzureId } from "../../../../utility/helpers";
import EmptyPlaceholder from "../../../../components/EmptyPlaceholder/EmptyPlaceholder";
import ActionButtons from "../../../../components/Common/ActionButtons";

/**
 * This component allows a PACE user to be edited and for the new values to be fed back to the user management page.
 * @param {Object} user The PACE user record. This should be the locally stored record as this component does no manage the edited fields states.
 * @param {({userUpdates}) => void} addUserChanges A callback method for passing the new user changes back to the page.
 * @param {() => void} removeUserChanges A callback method for cancelling the local user changes.
 * @param {(Boolean) => void} setEdited A callback method for setting the user to display as edited.
 */
export const UserDetails = ({
	user,
	addUserChanges,
	removeUserChanges,
	setEdited,
	roleTypes,
	isTeamsSyncPipelineEnabled,
	isIntegrationsTeamsSyncEnabled,
	isPaceToTeamsSyncEnabled,
}) => {
	const [editableUser, setEditableUser] = React.useState(null);
	const [availableRoles, setAvailableRoles] = React.useState([]);
	const [currentRoles, setCurrentRoles] = React.useState([]);
	React.useEffect(() => {
		if (user) {
			const newAvailableRoles = { ...roleTypes };
			const newCurrentRoles = [];
			user.roles?.forEach((roleId) => {
				if (roleTypes[roleId]) {
					newCurrentRoles.push(roleTypes[roleId]);
				} else {
					newCurrentRoles.push({ roleName: "Unknown", roleId });
				}
				if (newAvailableRoles[roleId]) {
					delete newAvailableRoles[roleId];
				}
			});
			setCurrentRoles(newCurrentRoles);
			setAvailableRoles(
				Object.values(newAvailableRoles).sort((a, b) => (a.roleName.toLowerCase() > b.roleName.toLowerCase() ? 1 : -1)),
			);
			setEditableUser({ ...user, newUser: false });
		} else {
			setEditableUser(null);
		}
	}, [user, roleTypes]);

	const setUserValue = (fieldName, newValue) => {
		const newUser = { ...editableUser, edited: true };
		newUser[fieldName] = newValue;
		setEditableUser(newUser);
		addUserChanges(editableUser.userId, fieldName, newValue);
	};

	const handleEdit = () => {
		setUserValue("edited", true);
		setEdited(true);
	};
	const valueField = (fieldName, value) => (
		<div>
			<div style={{ fontWeight: "lighter", color: "gray", fontSize: 13 }}>{fieldName}</div>
			<div style={{ marginTop: "7px", textOverflow: "ellipsis", overflow: "hidden" }}>{value}</div>
		</div>
	);
	return (
		<CardContent>
			{!editableUser ? (
				<EmptyPlaceholder message="To get started, select a user from the list on the left." />
			) : (
				<>
					<div className="d-flex align-items-center justify-content-between flex-wrap">
						<h5 style={{ fontWeight: "bold", margin: "4px 0" }}>{editableUser.name || editableUser.email}</h5>
						<ActionButtons
							showCancel={editableUser.edited}
							onCancel={() => removeUserChanges(user.userId)}
							cancelLabel="Cancel User Changes"
						/>
					</div>
					<div style={{ display: "flex", margin: "20px 8px 0px 8px" }}>
						<Grid container spacing={3}>
							<Grid item xs={7}>
								<FormTextField
									styleObject={{ width: "100%" }}
									label={"Email"}
									placeholder=""
									formEdited={editableUser.edited}
									setEdited={handleEdit}
									toolTipMessage={USER_MANAGEMENT_TOOL_TIPS.EMAIL}
									startingValue={editableUser.email || ""}
									errorMessage={"Email cannot be empty."}
									errorCheck={(value) => {
										if (value && value.length > 0) {
											return false;
										}
										return true;
									}}
									setConfigValue={setUserValue}
									valueName={"email"}
								/>
							</Grid>
							<Grid item xs={5}>
								{valueField("Connect Username", editableUser.username || "No Amazon Connect user found.")}
							</Grid>
						</Grid>
					</div>
					<div className="align-items-center justify-content-between" style={{ padding: " 0px 5px", marginTop: 20 }}>
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								margin: "20px 0px 20px 0px",
							}}
						>
							<Grid container spacing={3}>
								<Grid item xs={7}>
									<FormTextField
										styleObject={{ width: "100%" }}
										label={"Azure Active Directory ID (Teams Integration)"}
										placeholder=""
										formEdited={editableUser.edited}
										setEdited={handleEdit}
										toolTipMessage={USER_MANAGEMENT_TOOL_TIPS.ACTIVE_DIRECTORY_ID}
										startingValue={editableUser.azureId || ""}
										errorMessage={"Warning: Possible Invalid ID Format"}
										errorCheck={(value) => {
											if (value && value.length > 0) {
												return !isValidAzureId(value);
											}
											return false;
										}}
										setConfigValue={setUserValue}
										valueName={"azureId"}
									/>
								</Grid>
								<Grid item xs={5}>
									<div>
										<Typography style={{ fontSize: 12, color: "gray" }}>Sync Teams Status</Typography>
										<Tooltip
											arrow
											interactive
											placement={"bottom-start"}
											title={CONFIG_TOOL_TIPS.ENABLE_TEAMS_STATUS_AGENT_SYNC}
										>
											<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
										</Tooltip>
										<Checkbox
											checked={
												isTeamsSyncPipelineEnabled &&
												(isIntegrationsTeamsSyncEnabled || isPaceToTeamsSyncEnabled) &&
												!!editableUser.azureId &&
												(editableUser.isTeamsStatusSyncEnabled || false)
											}
											disabled={
												!editableUser.azureId ||
												!isTeamsSyncPipelineEnabled ||
												(!isIntegrationsTeamsSyncEnabled && !isPaceToTeamsSyncEnabled)
											}
											onChange={(event) => {
												handleEdit();
												setUserValue("isTeamsStatusSyncEnabled", event.target.checked);
											}}
										/>
									</div>
								</Grid>
							</Grid>
						</div>
						<div>
							<Autocomplete
								options={availableRoles}
								onChange={(_event, value) => {
									const newRoles = [];
									const newAvailableRoles = { ...roleTypes };
									value.forEach((role) => {
										newRoles.push(role.roleId);
										if (newAvailableRoles[role.roleId]) {
											delete newAvailableRoles[role.roleId];
										}
									});
									setUserValue("roles", newRoles);
									setCurrentRoles(value);
									setAvailableRoles(
										Object.values(newAvailableRoles).sort((a, b) =>
											a.roleName.toLowerCase() > b.roleName.toLowerCase() ? 1 : -1,
										),
									);
								}}
								value={currentRoles || []}
								freeSolo
								getOptionLabel={(option) => option.roleName}
								fullWidth
								multiple
								renderTags={(value, getTagProps) => {
									return value.map((option, index) => {
										const error = !roleTypes[option.roleId || option];
										return (
											<Chip
												variant="outlined"
												label={option.roleName || option}
												{...getTagProps({ index })}
												color={error ? "secondary" : undefined}
											/>
										);
									});
								}}
								renderInput={(params) => {
									const error = !!currentRoles.find((x) => !roleTypes[x.roleId]);
									return (
										<TextField
											{...params}
											label="Roles"
											id="roleTextField"
											margin="dense"
											variant="outlined"
											size="small"
											value={(editableUser && editableUser.roles) || []}
											error={error}
											helperText={error ? "One or more of these roles no longer exists." : undefined}
										/>
									);
								}}
							/>
						</div>
					</div>
				</>
			)}
		</CardContent>
	);
};
